import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackLink } from "@/components/BackLink";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6">
      <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</div>
      <div className="font-display text-3xl text-forest mt-2">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [a, setA] = useState(null);

  useEffect(() => { api.orgAnalytics().then(setA).catch(() => setA(null)); }, []);

  if (!user || user.user_type !== "organisation") {
    return <div className="max-w-2xl mx-auto px-6 py-16">
      <h1 className="font-display text-2xl text-forest">Analytics is for organisations.</h1>
    </div>;
  }
  if (!a) return <div className="max-w-3xl mx-auto px-6 py-16 text-neutral-500">Loading analytics…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="analytics-page">
      <Seo title="Analytics" />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Insights</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Impact analytics</h1>

      <a href={api.exportCsvUrl()} data-testid="analytics-csv" className="inline-block mt-4 text-sm text-forest underline" download>↓ Download CSV (hours export)</a>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8">
        <Stat label="Hours mobilised" value={a.total_hours} />
        <Stat label="Volunteers" value={a.total_volunteers} />
        <Stat label="Approval rate" value={`${a.approval_rate}%`} sub={`${a.approved_logs}/${a.total_logs} logs`} />
        <Stat label="Open roles" value={a.open_opportunities} sub={`${a.total_applicants} applicants`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
        <Stat label="Skill match rate" value={`${a.skill_match_rate ?? 0}%`} sub="Applicants whose skills overlap the role" />
        <Stat label="Retention" value={`${a.retention_rate ?? 0}%`} sub="Volunteers active in 2+ weeks" />
        <Stat label="Top cause" value={a.hours_by_value?.[0]?.value || "—"} sub={a.hours_by_value?.[0] ? `${a.hours_by_value[0].hours}h` : ""} />
      </div>

      <Card className="mt-8 border-neutral-200">
        <CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold mb-4">Approved hours · last 8 weeks</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a.hours_by_week}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#5c5c5c" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5c5c5c" }} />
                <Tooltip />
                <Bar dataKey="hours" fill="#1A4331" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {a.hours_by_week.length === 0 && <div className="text-neutral-500 text-sm">No approved hours yet.</div>}
          </div>
        </CardContent>
      </Card>

      <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Hours by cause</h2>
      <div className="flex flex-wrap gap-2 mt-4">
        {(a.hours_by_value || []).map(c => (
          <div key={c.value} className="bg-white border border-neutral-200 rounded-full px-4 py-2 text-sm capitalize text-forest">
            {c.value} · <span className="font-semibold">{c.hours}h</span>
          </div>
        ))}
        {(!a.hours_by_value || a.hours_by_value.length === 0) && <div className="text-sm text-neutral-500">No cause data yet.</div>}
      </div>

      <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Top contributors</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {a.top_contributors.map((c, i) => (
          <Link key={c.user_id} to={`/u/${c.user_id}`} className="bg-white border border-neutral-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform flex items-center gap-4">
            <div className="w-9 h-9 rounded-full bg-forest/10 flex items-center justify-center text-forest font-display font-semibold">#{i + 1}</div>
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-forest text-white text-sm">{c.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="font-medium text-forest">{c.name}</div>
              <div className="text-xs text-neutral-500">{c.hours} approved hours</div>
            </div>
          </Link>
        ))}
        {a.top_contributors.length === 0 && <div className="text-sm text-neutral-500">No contributors yet.</div>}
      </div>
    </div>
  );
}
